import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '@/shared/config/firebase';
import { validateOrganizationLogoFile } from '@/shared/utils/organization-logo';

/**
 * Service pour gérer les téléchargements vers Firebase Storage
 */
export class StorageService {
  /**
   * Télécharge un fichier vers Firebase Storage
   * @param file - Le fichier à télécharger
   * @param path - Le chemin de destination dans le storage
   * @returns L'URL publique du fichier téléchargé
   */
  static async uploadFile(file: File, path: string): Promise<string> {
    try {
      console.log(`📤 Upload du fichier vers: ${path}`);
      
      // Créer une référence vers le fichier dans le storage
      const storageRef = ref(storage, path);
      
      // Télécharger le fichier
      const snapshot = await uploadBytes(storageRef, file);
      
      // Obtenir l'URL de téléchargement
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      console.log(`✅ Fichier uploadé avec succès: ${downloadURL}`);
      return downloadURL;
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'upload du fichier:', error);
      throw new Error('Échec du téléchargement du fichier');
    }
  }

  /**
   * Télécharge un logo d'organisation vers Firebase Storage
   * @param file - Le fichier logo
   * @param organizationId - L'ID de l'organisation
   * @returns L'URL publique du logo
   */
  static async uploadOrganizationLogo(file: File, organizationId: string): Promise<string> {
    try {
      validateOrganizationLogoFile(file);

      // Générer un nom de fichier unique
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `logo-${organizationId}-${timestamp}.${fileExtension}`;
      const storagePath = `logos/${fileName}`;

      return await this.uploadFile(file, storagePath);
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'upload du logo:', error);
      throw error;
    }
  }

  /**
   * Supprime un fichier du storage
   * @param path - Le chemin du fichier à supprimer
   */
  static async deleteFile(path: string): Promise<void> {
    try {
      console.log(`🗑️ Suppression du fichier: ${path}`);
      
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
      
      console.log(`✅ Fichier supprimé avec succès: ${path}`);
      
    } catch (error) {
      console.error('❌ Erreur lors de la suppression du fichier:', error);
      throw new Error('Échec de la suppression du fichier');
    }
  }

  /**
   * Extrait le chemin du storage à partir d'une URL Firebase Storage
   * @param url - L'URL Firebase Storage
   * @returns Le chemin du fichier dans le storage
   */
  static extractStoragePathFromUrl(url: string): string | null {
    try {
      // Format d'URL Firebase Storage: https://firebasestorage.googleapis.com/v0/b/bucket/o/path%2Ffile?alt=media&token=...
      const regex = /\/o\/(.+?)\?/;
      const match = regex.exec(url);
      if (match) {
        return decodeURIComponent(match[1]);
      }
      return null;
    } catch (error) {
      console.error('❌ Erreur lors de l\'extraction du chemin:', error);
      return null;
    }
  }
}
